import React, { useMemo } from 'react'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { arePricesLoading, getCheckoutDate, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import useQueryParams from 'hooks/useQueryParams'
import useLPCUniqueOptions from 'hooks/OfferPage/useLPCUniqueOptions'
import { initialRooms } from 'lib/offer/occupancyUtils'
import uuidV4 from 'lib/string/uuidV4Utils'
import AlwaysOnRateOptionMobile from 'components/OfferPage/AlwaysOn/MobileBookingFlow/AlwaysOnRateOptionMobile'
import BedbankRoomOptionRate from 'components/OfferPage/Bedbank/BedbankRoomOptionRate'
import { getCombinedRates, getRateListKey } from '../singlePropertyUtils'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import usePropertyRatesList from 'hooks/usePropertyRatesList'
import Pane from 'components/Common/Pane'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { pluralizeToString } from 'lib/string/pluralize'
import Group from 'components/utils/Group'

const OptionsContainer = styled.div`
  padding: ${rem(12)} ${rem(20)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
`
interface Props {
  offer: App.Offer;
  bedbankOffer?: App.BedbankOffer;
  packageViews: Array<OfferPackageOptionView>;
  bedbankViews: Array<App.BedbankOfferPackageView>;
  checkIn?: string;
  checkOut?: string;
  rooms: Array<App.Occupants>;
  flightDestination?: App.Airport;
  previewMode: boolean;
  roomRateId?: string;
  pricesLoading?: boolean;
  duration?: number;
  airport?: App.Airport;
  roomListId: string;
}

function PropertyRoomOptionMobile(props: Props) {
  const {
    offer,
    bedbankOffer,
    packageViews,
    checkIn,
    checkOut,
    rooms,
    flightDestination,
    previewMode,
    roomRateId,
    pricesLoading,
    duration,
    bedbankViews,
    roomListId,
  } = props

  const utmContent = useQueryParams().get('utm_content')

  const options = useMemo(() => {
    if (previewMode) {
      // Preview mode is for content people and will show unavailable rates so they can validate them
      // as the rates may not have had prices and such loaded yet
      return packageViews
    }
    const requiresRates = offer.property?.useDynamicCancellationPolicies && !!checkIn && !pricesLoading
    return packageViews.filter(view => view.capacityValid && view.hotelAvailable && (!requiresRates || view.hasAvailableRates))
  }, [checkIn, offer, packageViews, previewMode, pricesLoading])

  const useRatesEndpoint = useOptimizelyExperiment(OptimizelyFeatureFlags.accomUsePropertyPageRatesEndpoint)
  const availableOptions = useLPCUniqueOptions(
    offer.id,
    options,
    {
      useDynamicCancellationPolicies: offer.property?.useDynamicCancellationPolicies ?? false,
      duration,
      checkIn,
      rooms: rooms ?? initialRooms,
      showAll: useRatesEndpoint,
    },
  )

  const [propertyRates] = usePropertyRatesList(offer.property?.id, {
    checkIn,
    checkOut,
    occupancies: rooms ?? initialRooms,
    disabled: !useRatesEndpoint,
  })

  const displayRates = useMemo(() => {
    const combinedRates = getCombinedRates(
      packageViews,
      bedbankViews,
      availableOptions,
      {
        previewMode,
        showAll: useRatesEndpoint,
        roomRateId,
        utmContent,
      },
    )

    if (useRatesEndpoint && checkIn && checkOut) {
      const rateIds = new Set(propertyRates.map(rate => rate.key))
      return combinedRates.filter(rate => rateIds.has(getRateListKey(rate)))
    }
    return combinedRates
  }, [availableOptions, bedbankViews, checkIn, checkOut, packageViews, previewMode, propertyRates, roomRateId, useRatesEndpoint, utmContent])

  const rateListId = useMemo(() => uuidV4(),
  // eslint-disable-next-line react-hooks/exhaustive-deps
    [bedbankViews]) // regenerate id if packages change

  return (
    <Pane background="neutral-six">
      <Group direction="vertical" gap={8}>
        {displayRates.length > 1 && (
          <OptionsContainer>
            <BodyText variant="small" colour="neutral-three">{pluralizeToString('package option', displayRates.length)} available</BodyText>
          </OptionsContainer>
        )}
        {displayRates.map((view, index) => {
          if (view.lpcRate) {
            return <Pane key={view.lpcRate.package.uniqueKey}>
              <AlwaysOnRateOptionMobile
                offer={offer}
                optionNumber={index + 1}
                packageView={view.lpcRate}
                flightDestination={flightDestination}
                showOptionLabel={displayRates.length > 1}
              />
            </Pane>
          } else if (view.bedbankRate && bedbankOffer) {
            return <Pane key={view.bedbankRate.rate.id}>
              <BedbankRoomOptionRate
                offer={bedbankOffer}
                packageView={view.bedbankRate.view}
                rate={view.bedbankRate.rate}
                optionNumber={index + 1}
                roomPosition={index + 1}
                roomListId={roomListId}
                rateListId={rateListId}
                showOptionLabel={displayRates.length > 1}
              />
            </Pane>
          }
          return null
        })}
      </Group>
    </Pane>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  rooms: state.rooms,
  checkIn: state.checkInDate,
  checkOut: getCheckoutDate(state),
  duration: state.duration,
  previewMode: appState.system.previewMode,
  roomRateId: state.roomOption?.roomRateId,
  pricesLoading: arePricesLoading(appState, state),
  airport: state.airport,
}))(PropertyRoomOptionMobile)
